import Model from '@/store/plugins/orm/Model'
import { Order, CashRegisterTransaction, UserTransaction, Product, Client, User, Shift, KitchenTicket } from './ModelRepository'
import Store from '@/store';
import HashGen from '@/utilities/HashGen';
import ApiService from '@/services/ApiService';
import LocalNetworkService from '@/services/LocalNetworkService';
import CashRegisterService from '@/services/CashRegisterService';
import PrintService from '@/print/PrintService';
import Terminal from '@/models/Terminal';

export class AppEvent extends Model {
    static get entity () {
        return 'app_events'
    }

    static get primaryKey() {
        return 'hash';
    }

    static defaultCallback = undefined
    static responseHandlers = new Map();

    static fields () {
        return {
            hash: this.attr(HashGen.regular()),
            venue_hash: this.attr(''),
            type: this.attr(null),
            local: this.attr(false),
            sender: this.attr(''),
            recipient: this.attr(''),
            request_hash: this.attr(''),
            response_required: this.attr(false),
            data: this.attr({}),
        }
    }

    static build(type, data) {
        return new AppEvent({ type, data, venue_hash: Store.getters.tenant?.venue?.hash || '' })
    }

    overLocalNetwork() {
        this.local = true
        return this
    }

    withResponse() {
        this.response_required = true
        return this
    }

    async send(recipient, timeout = 2000) {
        if(Store.getters.device?.platform === 'android' && isAllegra) return [{ success: false, terminal: undefined }]

        const terminal = await Terminal.current()

        if(terminal) this.sender = terminal.key
        if(recipient) this.recipient = recipient

        this.save()

        if (!this.local && Store.getters.isOnline) {
            ApiService.post('/push-notifications', this.$toJson())
        }

        const lanEventResponse = await this.sendOverLocalNetwork()

        if(this.local && lanEventResponse.status !== 'sent') {
          return [{ success: false, terminal: undefined }]
        }

        if(!this.response_required) {
          return [{ success: true, terminal: undefined }]
        }

        return new Promise((resolve, reject) => {
            let responses = []

            // Setup a handler to resolve upon response
            AppEvent.responseHandlers.set(this.hash, (response) => {
                if(!recipient) {
                  responses.push(response)
                  return
                }

                clearTimeout(timeoutId)
                resolve([response])
            })

            const timeoutId = setTimeout(() => {
                AppEvent.responseHandlers.delete(this.hash)

                if(responses.length > 0) resolve(responses)
                else resolve([{ success: false, error: 'timeout', terminal: undefined }])
            }, timeout)
        })
    }

    async respond(data) {
        const terminal = await Terminal.current()

        const appEvent = new AppEvent({
            type: this.type,
            data,
            local: this.local,
            response_required: false,
            request_hash: this.hash,
            sender: terminal?.key || '',
            recipient: this.sender
        })

        appEvent.save()

        if (!this.local && Store.getters.isOnline) {
            ApiService.post('/push-notifications', appEvent.$toJson())
        }

        const lanEventResponse = await appEvent.sendOverLocalNetwork()

        return { success: !this.local || lanEventResponse.status === 'sent' }
    }

    async sendOverLocalNetwork() {
        if(!LocalNetworkService.connectionStatus?.connected || !LocalNetworkService.connectionStatus?.terminals.length) {
            return { status: 'failed', error: 'not_connected' }
        }

        return LocalNetworkService.send(this)
    }

    static async handle(event) {
        console.log('Received Event', event)

        if(Store.getters.tenant?.venue?.hash !== event.venue_hash) return

        const existing = AppEvent.all().find(ev => ev.hash === event.hash)
        if(existing) return false

        const appEvent = new AppEvent(event)
        appEvent.save()

        const handler = this.responseHandlers.get(event.request_hash)
        if (handler) {
            handler(event.data)
            this.responseHandlers.delete(event.request_hash)
            return false
        }

        // unhandled response
        if(event.request_hash) return false

        let proceed = true

        if (event.type === 'printServerUpdate') {
            await AppEvent.onPrintServerUpdate(event)
            proceed = false
        }

        if (event.type === 'cashRegisterTransactionAdd') {
            await AppEvent.onCashRegisterTransactionAdd(appEvent)
        }

        if (event.type === 'userUpdate') {
            await AppEvent.onUserUpdate(appEvent)
        }

        if (event.type === 'shiftUpdate') {
            await AppEvent.onShiftUpdate(appEvent)
        }

        if (event.type === 'orderUpdate') {
            await AppEvent.onOrderUpdate(appEvent)
        }

        if (event.type === 'orderDelete') {
            await AppEvent.onOrderDelete(event)
        }

        if (event.type === 'ordersSync') {
            await Order.onOrdersSync(event)
        }

        if (event.type === 'kitchenTicketsSync') {
            await KitchenTicket.onKitchenTicketsSync(event)
        }

        if (AppEvent.defaultCallback) {
            await AppEvent.defaultCallback(appEvent)
        }

        return proceed
    }

    save() {
        const limit = 100

        if(AppEvent.all().length >= limit) {
            AppEvent.create({ data: AppEvent.all().slice(limit / 2).map(ev => ev.$toJson()) })
        }

        this.$save()
    }

    static registerDefaultCallback(callback) {
        AppEvent.defaultCallback = callback
    }

    static async onCashRegisterTransactionAdd(event) {
        const isOnline = Store.getters.isOnline;
        if(!isOnline) return

        const cashRegisterId = CashRegisterService.getId()
        if(!cashRegisterId || event.data.cash_register_id !== +cashRegisterId) return

        const response = await ApiService.get(`/cash-registers/transactions/${event.data.id}`)

        CashRegisterTransaction.insert({ data: [response.data.cash_register_transaction] });
    }

    static async onUserUpdate(event) {
        const isOnline = Store.getters.isOnline;
        if(!isOnline) return

        const response = await ApiService.get(`/users/${event.data.id}`)

        User.insert({ data: [response.data.user] });
        UserTransaction.insert({ data: response.data.user_transactions });
    }

    static async onShiftUpdate(event) {
        const isOnline = Store.getters.isOnline;
        if(!isOnline) return

        const cashRegisterId = CashRegisterService.getId()
        if(!cashRegisterId || event.data.cash_register_id !== +cashRegisterId) return

        const response = await ApiService.get('/shift/status')

        Shift.create({ data: [response.data.shift] });

        let curShift = Shift.current()

        if(!curShift){
            User.create({ data: response.data.users });
        }
    }

    static async onOrderUpdate(event) {
        const isOnline = Store.getters.isOnline;
        if(!isOnline) return

        Product.update(event.data.product_counts)

        const orderIds = [event.data.order, ...event.data.ref_orders]
        const clientId = event.data.client

        const response = await ApiService.get('/orders/issue-result', {
            params: {
                order_ids: orderIds,
                client_id: clientId,
            }
        })

        Order.insert({ data: response.data.orders, freeze: true })
        Client.insert({ data: [response.data.client] })
    }

    static async onPrintServerUpdate(event) {
        PrintService.init();
    }

    static async onOrderDelete(event) {
        const order = Order.all().find(o => o.id === event.data.id)
        if(!order) return
        order.$delete()
    }
}

export default AppEvent
